import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Tooltip,
  IconButton,
  Alert,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import EmptyState from '../EmptyState';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';

const UserManagement = () => {
  const [users, setUsers] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server must search there instead.
  const {
    query, setQuery, filterValues, setFilter, reset,
    rows: visibleUsers, resultCount, totalCount
  } = useTableSearch(users, {
    searchFields: ['name', 'email', 'phone', 'role'],
    filters: {
      role: (row, v) => row.role === v,
      isActive: (row, v) => String(row.isActive !== false) === v
    }
  });
  const [roles, setRoles] = useState([]); // active roles (system + custom) for the role pickers

  // Create user dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', role: 'tenant', phone: '' });

  // Edit user dialog
  const [editDialog, setEditDialog] = useState({ open: false, userId: null });
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', role: '' });
  const [editSaving, setEditSaving] = useState(false);

  // Admin direct set-password dialog
  const [setPasswordDialog, setSetPasswordDialog] = useState({ open: false, userId: null, userEmail: '' });
  const [setPasswordForm, setSetPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [setPasswordLoading, setSetPasswordLoading] = useState(false);

  // Credentials dialog (shown after create / reset-email / set-password)
  const [credentials, setCredentials] = useState(null); // { email, password, action, note }

  useEffect(() => { fetchUsers(); fetchRoles(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users');
      setUsers(response.data);
    } catch {
      toast.error('Error fetching users');
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get('/roles?isActive=true');
      setRoles(response.data);
    } catch {
      toast.error('Error fetching roles');
    }
  };

  const roleLabel = (roleName) => roles.find((r) => r.name === roleName)?.displayName || roleName;

  // ── Create user ───────────────────────────────────────────────
  const handleOpenCreate = () => {
    setFormData({ name: '', email: '', role: 'tenant', phone: '' });
    setCreateOpen(true);
  };

  const handleCreateSubmit = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }
    try {
      const res = await axios.post('/auth/register', formData);
      setCreateOpen(false);
      fetchUsers();
      setCredentials({
        email: res.data.email,
        password: res.data.tempPassword,
        action: 'created',
        note: res.data.message,
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating user');
    }
  };

  // ── Edit user (name/email/phone) ───────────────────────────────
  const handleOpenEdit = (user) => {
    setEditForm({ name: user.name || '', email: user.email || '', phone: user.phone || '', role: user.role || '' });
    setEditDialog({ open: true, userId: user.id || user._id });
  };

  const handleEditSubmit = async () => {
    if (!editForm.name || !editForm.email) {
      toast.error('Name and email are required');
      return;
    }
    setEditSaving(true);
    try {
      await axios.put(`/users/${editDialog.userId}`, editForm);
      toast.success('User updated');
      setEditDialog({ open: false, userId: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating user');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Reset password (auto-generate + email) ────────────────────
  const handleResetPassword = async (userId, userEmail) => {
    if (!window.confirm(`Reset password for ${userEmail}?\n\nA temporary password will be generated and emailed.`)) return;
    try {
      const res = await axios.post(`/users/${userId}/reset-password`);
      setCredentials({
        email: userEmail,
        password: res.data.tempPassword,
        action: 'reset',
        note: res.data.message,
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error resetting password');
    }
  };

  // ── Set password (admin direct — no email) ────────────────────
  const openSetPassword = (userId, userEmail) => {
    setSetPasswordForm({ newPassword: '', confirmPassword: '' });
    setShowNewPassword(false);
    setSetPasswordDialog({ open: true, userId, userEmail });
  };

  const handleSetPasswordSubmit = async () => {
    const { newPassword, confirmPassword } = setPasswordForm;
    if (!newPassword) { toast.error('Password is required'); return; }
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }

    setSetPasswordLoading(true);
    try {
      await axios.put(`/users/${setPasswordDialog.userId}/set-password`, { newPassword });
      setSetPasswordDialog({ open: false, userId: null, userEmail: '' });
      fetchUsers();
      setCredentials({
        email: setPasswordDialog.userEmail,
        password: newPassword,
        action: 'set',
        note: 'No email was sent — share these credentials with the user directly.',
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error setting password');
    } finally {
      setSetPasswordLoading(false);
    }
  };

  // ── Deactivate ────────────────────────────────────────────────
  const handleDeactivate = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    try {
      await axios.delete(`/users/${userId}`);
      toast.success('User deactivated');
      fetchUsers();
    } catch {
      toast.error('Error deactivating user');
    }
  };

  const copyToClipboard = (text) =>
    navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard'));

  const getRoleColor = (role) => ({ admin: 'error', staff: 'primary', tenant: 'success' }[role] || 'default');

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">User Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Create User
        </Button>
      </Box>

      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Name, email or phone"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
        filters={[
            { key: 'role', label: 'Role', value: filterValues.role || '',
              onChange: (v) => setFilter('role', v),
              options: [...new Set(users.map((u) => u.role))].filter(Boolean).map((r) => ({ value: r, label: r })) },
            { key: 'isActive', label: 'Status', value: filterValues.isActive || '',
              onChange: (v) => setFilter('isActive', v), allLabel: 'All statuses',
              options: [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }] }
        ]}
      />
      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell className="pin-left">Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell className="pin-right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleUsers.map((user) => {
              const uid = user.id || user._id;
              return (
                <TableRow key={uid}>
                  <TableCell className="pin-left">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.phone || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip label={roleLabel(user.role)} color={getRoleColor(user.role)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        label={user.isActive ? 'Active' : 'Inactive'}
                        color={user.isActive ? 'success' : 'default'}
                        size="small"
                      />
                      {user.mustChangePassword && (
                        <Chip label="Temp Password" color="warning" size="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell className="pin-right">
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit Details">
                        <IconButton size="small" color="default"
                          onClick={() => handleOpenEdit(user)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset Password (sends email)">
                        <IconButton size="small" color="primary"
                          onClick={() => handleResetPassword(uid, user.email)}>
                          <LockResetIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Set Password (no email — for users without email access)">
                        <IconButton size="small" color="warning"
                          onClick={() => openSetPassword(uid, user.email)}>
                          <VpnKeyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {user.isActive && (
                        <Tooltip title="Deactivate User">
                          <IconButton size="small" color="error"
                            onClick={() => handleDeactivate(uid)}>
                            <PersonOffIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {visibleUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <EmptyState title="No users yet" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </ScrollTable>

      {/* ── Create User Dialog ─────────────────────────────────── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            A temporary password will be auto-generated and emailed to the user.
            They must set a permanent password on first login.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Full Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required type="email" label="Email Address"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                helperText="Login credentials will be sent to this address" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Phone Number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required select label="Role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                {roles.map((r) => (
                  <MenuItem key={r.name} value={r.name}>{r.displayName}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateSubmit} variant="contained">
            Create User & Send Email
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit User Dialog ───────────────────────────────────── */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, userId: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User Details</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Full Name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required type="email" label="Email Address"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Phone Number"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required select label="Role"
                value={editForm.role}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
                {roles.map((r) => (
                  <MenuItem key={r.name} value={r.name}>{r.displayName}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, userId: null })}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" disabled={editSaving}>
            {editSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Set Password Dialog (admin direct, no email) ────────── */}
      <Dialog open={setPasswordDialog.open}
        onClose={() => setSetPasswordDialog({ open: false, userId: null, userEmail: '' })}
        maxWidth="sm" fullWidth>
        <DialogTitle>Set Password</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
            Use this to recover an account when the user cannot receive email. No email will be
            sent — share the new password with the user directly (phone, in person, etc.).
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Setting password for: <strong>{setPasswordDialog.userEmail}</strong>
          </Typography>
          <TextField
            fullWidth required type={showNewPassword ? 'text' : 'password'}
            label="New Password" margin="normal"
            value={setPasswordForm.newPassword}
            onChange={(e) => setSetPasswordForm({ ...setPasswordForm, newPassword: e.target.value })}
            helperText="Minimum 8 characters. User will be forced to change this on next login."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            fullWidth required type="password"
            label="Confirm New Password" margin="normal"
            value={setPasswordForm.confirmPassword}
            onChange={(e) => setSetPasswordForm({ ...setPasswordForm, confirmPassword: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetPasswordDialog({ open: false, userId: null, userEmail: '' })}>
            Cancel
          </Button>
          <Button onClick={handleSetPasswordSubmit} variant="contained" color="warning"
            disabled={setPasswordLoading}>
            {setPasswordLoading ? 'Setting...' : 'Set Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Credentials Dialog (shown after create / reset / set) ─ */}
      <Dialog open={!!credentials} onClose={() => setCredentials(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {credentials?.action === 'created' ? 'User Created'
            : credentials?.action === 'reset' ? 'Password Reset'
            : 'Password Set'}
        </DialogTitle>
        <DialogContent>
          <Alert severity={credentials?.action === 'set' ? 'warning' : 'info'} sx={{ mb: 2 }}>
            {credentials?.note}
          </Alert>
          <Alert severity="info" sx={{ mb: 2 }}>
            Save these credentials now — the password will not be shown again.
          </Alert>
          <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Username (Email)</Typography>
              <IconButton size="small" onClick={() => copyToClipboard(credentials?.email || '')}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>{credentials?.email}</Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {credentials?.action === 'set' ? 'New Password' : 'Temporary Password'}
              </Typography>
              <IconButton size="small" onClick={() => copyToClipboard(credentials?.password || '')}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="h6"
              sx={{ fontFamily: 'monospace', letterSpacing: 3, color: 'error.main' }}>
              {credentials?.password}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCredentials(null)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UserManagement;
